/**
 * @lodex-pro/web-portal/routes/MyTanks — the tanks a pumper or foreman looks after (OPP PR 8).
 *
 * Under OP_ROUTES_PUMPERS. Pumpers and foremen see only the tanks they select in Tickets,
 * Volumes and Tanks (a foreman also sees his routes' tanks without selecting them); whoever
 * selects a tank is one of its contacts.
 *
 *   - PUMPER: their own list, grouped by well (or battery), searchable; tick a tank, or every
 *     tank on a well at once.
 *   - FOREMAN: the same, plus a picker to choose tanks for one of his pumpers (default
 *     himself), and the tanks on his routes that have no contact.
 *   - OPERATOR_ADMIN ("Contacts"): no list of his own; he picks a pumper or foreman first, then
 *     edits that person's list. Plus every tank in the company with no contact, and the
 *     requestor matches (OPP PR 10, RequestorMatches.tsx).
 *
 * OPP PR 10: a pumper or foreman viewing their own list is offered the tanks at the wells they
 * request loads for ("Become a contact"). Offers only, never selected for them.
 *
 * Without OP_ROUTES_PUMPERS the page says so in plain words (D8), not with plan names. The API
 * checks the role and the company (SELECT_OPERATOR_TANKS); this is the UX, not the gate.
 */

import React from 'react';
import type {
  RequestorWellOfferView,
  SelectableTankView,
  SelectionPersonView,
  UncoveredTankView,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { ApiError } from '@lodex-pro/web-shared';
import { usePortalMe } from '../hooks/useOperatorPortal';
import { useRequestorOffers } from '../hooks/useRequestorLinks';
import { RequestorMatches } from './RequestorMatches';
import {
  useCoverage,
  useSelectableTanks,
  useSelectionPeople,
  useSelectTanks,
} from '../hooks/useTankSelections';
import { PortalDataError } from '../components/PortalDataError';
import { portalErrorText } from '../components/portal-error-text';
import { portalIsAdmin, portalIsForeman, portalLacks } from '../nav/portal-features';
import {
  BUTTON,
  CARD,
  INPUT,
  MUTED,
  TAG,
  placeKey,
  placeLabel,
  plural,
} from './operator-tank-labels';

/** D8 — what a pumper, foreman or admin reads when the plan has no tank selection. */
export const SELECTION_OFF_MESSAGE = "Tank selection isn't in your company's plan; ask your admin.";

const SCOPE_TEXT =
  'You see the tanks you select (and, for a foreman, the tanks on your routes) in Tickets, ' +
  'Volumes and Tanks.';

const ERROR_TEXT: React.CSSProperties = { color: 'var(--color-danger)', fontSize: '13px' };
const WARN_TAG: React.CSSProperties = {
  ...TAG,
  background: 'rgba(246, 182, 94, 0.14)',
  border: '1px solid rgba(246, 182, 94, 0.4)',
  color: 'var(--color-warning)',
};
const ROUTE_TAG: React.CSSProperties = {
  ...TAG,
  background: 'rgba(111, 207, 142, 0.14)',
  color: 'var(--color-success)',
};
const DISPOSAL_TAG: React.CSSProperties = {
  ...TAG,
  background: 'rgba(123, 150, 232, 0.18)',
  color: 'var(--color-accent-blue-dark)',
};

interface Viewer {
  readonly userId: string;
  readonly isAdmin: boolean;
  readonly isForeman: boolean;
}

function isFeatureOff(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'FEATURE_NOT_ENABLED';
}

function SelectionOff(): React.JSX.Element {
  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '12px' }}>My tanks</h1>
      <p role="status">{SELECTION_OFF_MESSAGE}</p>
    </main>
  );
}

export function MyTanks(): React.JSX.Element {
  const me = usePortalMe();
  if (me.data === undefined) {
    return (
      <main style={{ padding: '24px' }}>
        {me.isError ? (
          <p role="alert">Could not load your tanks.</p>
        ) : (
          <p role="status">Loading…</p>
        )}
      </main>
    );
  }
  if (portalLacks(me.data, 'OP_ROUTES_PUMPERS')) return <SelectionOff />;
  return (
    <MyTanksBody
      viewer={{
        userId: me.data.userId,
        isAdmin: portalIsAdmin(me.data),
        isForeman: portalIsForeman(me.data),
      }}
    />
  );
}

function personLabel(p: SelectionPersonView, viewerId: string): string {
  const name = p.name ?? p.email ?? 'Unnamed';
  if (p.userId === viewerId) return `Me (${name})`;
  const role = p.role === 'FOREMAN' ? 'Foreman' : 'Pumper';
  return `${name} — ${role}, ${plural(p.selectedCount, 'tank')}`;
}

function MyTanksBody({ viewer }: { readonly viewer: Viewer }): React.JSX.Element {
  const choosesForOthers = viewer.isAdmin || viewer.isForeman;
  const people = useSelectionPeople(choosesForOthers);
  // '' = the admin has chosen nobody yet / the foreman is choosing for himself.
  const [personId, setPersonId] = React.useState('');
  const forSelf = !viewer.isAdmin && (personId === '' || personId === viewer.userId);
  const forUserId = forSelf ? undefined : personId === '' ? undefined : personId;
  const listEnabled = !viewer.isAdmin || personId !== '';
  const tanks = useSelectableTanks(forUserId, listEnabled);
  const coverage = useCoverage(choosesForOthers);
  // OPP PR 10: tanks at the wells this pumper/foreman requests loads for. Offers only; an
  // admin has none. Shown only on the caller's own list.
  const offers = useRequestorOffers(!viewer.isAdmin);

  if (isFeatureOff(tanks.error) || isFeatureOff(people.error)) return <SelectionOff />;

  const personList = people.data?.people ?? [];
  const chosen = personList.find((p) => p.userId === personId) ?? null;

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>{viewer.isAdmin ? 'Contacts' : 'My tanks'}</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        {viewer.isAdmin
          ? 'Choose the tanks each pumper or foreman looks after. Whoever looks after a tank is one of its contacts, and sees it in Tickets, Volumes and Tanks.'
          : SCOPE_TEXT}
      </p>

      {choosesForOthers && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
          <label htmlFor="my-tanks-person" style={{ fontSize: '14px' }}>
            Choose tanks for
          </label>
          <select
            id="my-tanks-person"
            value={viewer.isAdmin ? personId : personId === '' ? viewer.userId : personId}
            onChange={(e): void => {
              setPersonId(e.target.value);
            }}
            style={{ ...INPUT, minWidth: '280px' }}
          >
            {viewer.isAdmin && <option value="">Choose a pumper or foreman…</option>}
            {!viewer.isAdmin && !personList.some((p) => p.userId === viewer.userId) && (
              <option value={viewer.userId}>Me</option>
            )}
            {personList.map((p) => (
              <option key={p.userId} value={p.userId}>
                {personLabel(p, viewer.userId)}
              </option>
            ))}
          </select>
          {people.isLoading && (
            <span role="status" style={{ ...MUTED, fontSize: '13px' }}>
              Loading people…
            </span>
          )}
        </div>
      )}
      {people.isError && (
        <PortalDataError
          error={people.error}
          fallback="Could not load the people you can choose for."
        />
      )}

      {viewer.isAdmin && personId === '' ? (
        <p style={{ ...MUTED, fontSize: '14px' }}>
          {people.data !== undefined && personList.length === 0
            ? 'Your company has no active pumpers or foremen yet.'
            : 'Choose a pumper or foreman to see and change the tanks they look after.'}
        </p>
      ) : (
        <>
          {forSelf && offers.data !== undefined && offers.data.offers.length > 0 && (
            <OffersBanner offers={offers.data.offers} />
          )}
          {tanks.isLoading && <p role="status">Loading tanks…</p>}
          {tanks.isError && (
            <PortalDataError error={tanks.error} fallback="Could not load the tanks." />
          )}
          {tanks.data !== undefined && (
            <TankList
              tanks={tanks.data.tanks}
              forUserId={forUserId}
              forSelf={forSelf}
              personName={chosen === null ? null : (chosen.name ?? chosen.email)}
            />
          )}
        </>
      )}

      {choosesForOthers && <CoveragePanel isAdmin={viewer.isAdmin} query={coverage} />}
      {viewer.isAdmin && <RequestorMatches />}
    </main>
  );
}

// ── Offers (OPP PR 10) ───────────────────────────────────────────────────────

function offerPlace(o: RequestorWellOfferView): string {
  if (o.wellId !== null) {
    const name = o.wellName !== null && o.wellName.trim() !== '' ? o.wellName : 'Unnamed well';
    return o.apiNumber === null ? name : `${name} (API ${o.apiNumber})`;
  }
  if (o.siteLabel !== null && o.siteLabel.trim() !== '') return `Battery: ${o.siteLabel}`;
  return 'No well or battery';
}

/**
 * "Become a contact" for the tanks at wells the caller requests loads for. Nothing is
 * selected until the pumper presses the button: their own choice sets responsibility.
 */
function OffersBanner({
  offers,
}: {
  readonly offers: readonly RequestorWellOfferView[];
}): React.JSX.Element {
  const select = useSelectTanks();
  const [error, setError] = React.useState<string | null>(null);

  const accept = async (o: RequestorWellOfferView): Promise<void> => {
    setError(null);
    try {
      await select.mutateAsync({ tankIds: o.tankIds, selected: true });
    } catch (err) {
      setError(portalErrorText(err, 'Could not add those tanks.'));
    }
  };

  return (
    <section
      aria-label="Wells you request loads at"
      style={{ ...CARD, marginBottom: '16px', border: '1px solid var(--color-accent-blue)' }}
    >
      <p style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 600 }}>
        You request loads at these wells. Become a contact for their tanks?
      </p>
      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {offers.map((o) => {
          const place = offerPlace(o);
          return (
            <li
              key={o.wellId ?? `battery:${o.siteLabel ?? ''}`}
              aria-label={`Offer ${place}`}
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '6px 0',
                borderTop: '1px solid var(--color-border-primary)',
                fontSize: '14px',
              }}
            >
              <span style={{ fontWeight: 600 }}>{place}</span>
              <span style={{ ...MUTED, fontSize: '13px' }}>{o.tankNames.join(', ')}</span>
              <button
                type="button"
                style={{ ...BUTTON, marginLeft: 'auto' }}
                disabled={select.isPending}
                onClick={(): void => {
                  void accept(o);
                }}
              >
                Become a contact
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── The list ─────────────────────────────────────────────────────────────────

interface TankGroup {
  readonly key: string;
  readonly label: string;
  readonly wellId: string | null;
  readonly apiNumber: string | null;
  readonly isDisposal: boolean;
  readonly tanks: SelectableTankView[];
}

function groupTanks(tanks: readonly SelectableTankView[]): TankGroup[] {
  const groups = new Map<string, TankGroup>();
  for (const t of tanks) {
    const key = placeKey(t);
    const g = groups.get(key);
    if (g === undefined) {
      groups.set(key, {
        key,
        label: placeLabel(t),
        wellId: t.wellId,
        apiNumber: t.apiNumber,
        isDisposal: t.isDisposal,
        tanks: [t],
      });
    } else {
      g.tanks.push(t);
    }
  }
  const list = [...groups.values()];
  for (const g of list) g.tanks.sort((a, b) => a.name.localeCompare(b.name));
  return list.sort((a, b) => a.label.localeCompare(b.label));
}

function tankMatches(t: SelectableTankView, q: string): boolean {
  if (q === '') return true;
  const hay = [t.name, t.wellName, t.apiNumber, t.siteLabel]
    .filter((s): s is string => s !== null)
    .join(' ')
    .toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .every((part) => hay.includes(part));
}

function contactsText(t: SelectableTankView): string {
  return `Contacts: ${t.contacts.map((c) => c.name ?? 'Unnamed').join(', ')}`;
}

function TankList({
  tanks,
  forUserId,
  forSelf,
  personName,
}: {
  readonly tanks: readonly SelectableTankView[];
  readonly forUserId: string | undefined;
  readonly forSelf: boolean;
  readonly personName: string | null;
}): React.JSX.Element {
  const select = useSelectTanks();
  const [query, setQuery] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const matches = tanks.filter((t) => tankMatches(t, query.trim()));
  const groups = groupTanks(matches);
  const selectedCount = tanks.filter((t) => t.selected).length;
  const who = forSelf ? 'You look after' : `${personName ?? 'They'} looks after`;

  const send = async (
    target: { tankIds: string[] } | { wellIds: string[] },
    selected: boolean,
  ): Promise<void> => {
    setError(null);
    try {
      await select.mutateAsync({
        ...(forUserId === undefined ? {} : { forUserId }),
        ...target,
        selected,
      });
    } catch (err) {
      setError(portalErrorText(err, 'Could not change those tanks.'));
    }
  };

  return (
    <section aria-label="Tanks to look after">
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
        <input
          aria-label="Search tanks"
          placeholder="Search by tank, well or API number"
          value={query}
          onChange={(e): void => {
            setQuery(e.target.value);
          }}
          style={{ ...INPUT, width: '320px' }}
        />
        <span style={{ ...MUTED, fontSize: '13px' }}>
          {who} {plural(selectedCount, 'tank')}.
        </span>
      </div>
      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
      {tanks.length === 0 ? (
        <p style={{ ...MUTED, fontSize: '13px' }}>Your company has no tanks set up yet.</p>
      ) : groups.length === 0 ? (
        <p style={{ ...MUTED, fontSize: '13px' }}>No tanks match that search.</p>
      ) : (
        groups.map((g) => (
          <section key={g.key} aria-label={g.label} style={{ ...CARD, marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <h2 style={{ fontSize: '15px', margin: 0 }}>{g.label}</h2>
              {g.wellId !== null && g.apiNumber !== null && (
                <span style={{ ...MUTED, fontSize: '12px' }}>API {g.apiNumber}</span>
              )}
              {g.isDisposal && <span style={DISPOSAL_TAG}>Disposal well</span>}
              {g.wellId !== null && (
                <button
                  type="button"
                  style={{ ...BUTTON, marginLeft: 'auto' }}
                  disabled={select.isPending || g.tanks.every((t) => t.selected)}
                  onClick={(): void => {
                    if (g.wellId !== null) void send({ wellIds: [g.wellId] }, true);
                  }}
                >
                  Select all on this well
                </button>
              )}
            </div>
            <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
              {g.tanks.map((t) => (
                <li
                  key={t.tankId}
                  aria-label={`Tank ${t.name}`}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    padding: '6px 0',
                    borderTop: '1px solid var(--color-border-primary)',
                  }}
                >
                  <span style={{ minWidth: '160px', fontWeight: 600 }}>{t.name}</span>
                  <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={t.selected}
                      disabled={select.isPending}
                      onChange={(): void => {
                        void send({ tankIds: [t.tankId] }, !t.selected);
                      }}
                    />
                    <span style={{ fontSize: '13px' }}>
                      {forSelf ? 'I look after this tank' : 'Looks after this tank'}
                    </span>
                  </label>
                  {t.viaRoute && (
                    <span style={ROUTE_TAG}>{forSelf ? 'On your route' : 'On their route'}</span>
                  )}
                  {t.contacts.length === 0 ? (
                    <span style={WARN_TAG}>No contact yet</span>
                  ) : (
                    <span style={{ ...MUTED, fontSize: '13px' }}>{contactsText(t)}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </section>
  );
}

// ── Coverage ─────────────────────────────────────────────────────────────────

function CoveragePanel({
  isAdmin,
  query,
}: {
  readonly isAdmin: boolean;
  readonly query: ReturnType<typeof useCoverage>;
}): React.JSX.Element {
  const uncovered: UncoveredTankView[] = [...(query.data?.uncovered ?? [])].sort(
    (a, b) => placeLabel(a).localeCompare(placeLabel(b)) || a.name.localeCompare(b.name),
  );
  return (
    <section aria-label="Tanks with no contact" style={{ ...CARD, marginTop: '24px' }}>
      <h2 style={{ fontSize: '16px', margin: '0 0 4px' }}>Tanks with no contact</h2>
      <p style={{ ...MUTED, fontSize: '13px', margin: '0 0 8px' }}>
        {isAdmin
          ? 'Tanks in your company that nobody has selected yet.'
          : 'Tanks on your routes that nobody has selected yet.'}
      </p>
      {query.isLoading && <p role="status">Loading…</p>}
      {query.isError && (
        <PortalDataError error={query.error} fallback="Could not load the tanks with no contact." />
      )}
      {query.data !== undefined &&
        (uncovered.length === 0 ? (
          <p style={{ fontSize: '13px' }}>Every tank has a contact.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {uncovered.map((t) => (
              <li key={t.tankId} style={{ padding: '4px 0', fontSize: '14px' }}>
                {placeLabel(t)} — {t.name}
                {t.apiNumber !== null && (
                  <span style={{ ...MUTED, fontSize: '12px' }}> · API {t.apiNumber}</span>
                )}
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}
